import { cx } from '@/utils/cx'

/** PRD-017 §3.2 status vocabulary, Title case, one place (audit P2-11). */
export const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started',
  scheduled: 'Scheduled',
  running: 'Running',
  waiting_on_you: 'Waiting on you',
  review: 'Review',
  blocked: 'Blocked',
  deferred: 'Deferred',
  done: 'Done',
  open: 'Open',
  in_progress: 'In progress',
}

/**
 * v0.19 (Sean 2026-09-22; Seb UX review note 11, option (b), decision 8): a person's own `not_started` item reads
 * "With you" — the same word the drawer's guidance sentence already used ("Waiting on you: do it, then mark it
 * done"), so the badge and the sentence stop saying two things about one item. The status set and the internal
 * token are unchanged (PRD-017 §3.2 table; Q4 stays decided as Keep it out: no person-triggered Started state).
 * An agent's `not_started` item keeps reading "Not started" (it is waiting on a schedule or a dependency, not on
 * a person), so the swap is keyed on `assigneeKind`, never on the viewer's identity: this is the assignee's own
 * status word, read the same whether it is my item or someone else's row on `/work` or the Team → Work rollup.
 */
export function statusLabel(status: string, assigneeKind?: 'person' | 'agent' | null): string {
  if (status === 'not_started' && assigneeKind === 'person')
    return 'With you'
  return STATUS_LABEL[status] ?? status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
}

/** Hot = waiting on you or in review (moss); Blocked = amber; everything else quiet gray. */
export function StatusPill({ status, assigneeKind = null, fixed = false, className }: { status: string, assigneeKind?: 'person' | 'agent' | null, fixed?: boolean, className?: string }) {
  const hot = status === 'waiting_on_you' || status === 'review'
  const style = hot
    ? { background: 'var(--shell-hot-bg)', color: 'var(--shell-hot-fg)' }
    : status === 'blocked'
      ? { background: 'var(--color-bg-warning-secondary)', color: 'var(--color-text-warning-primary)' }
      : { background: 'var(--shell-quiet-bg)', color: 'var(--shell-quiet-fg)' }
  return (
    <span className={cx('inline-flex shrink-0 items-center justify-center rounded-full px-2 py-0.5 text-xs whitespace-nowrap', fixed && 'w-28', className)} style={style} data-testid="status-pill" data-status={status}>
      {statusLabel(status, assigneeKind)}
    </span>
  )
}
